import React, { useEffect, useState } from "react";
import {
  MediaModel,
  CountryWithStateModel,
  CreateSellerModel,
  MerchantAccount,
  SellerCommunicationSettingsModel,
  SellerGridModel,
  SellerLinkedMerchantAccountModel,
} from "../../../shared/models";
import { observer } from "mobx-react";
import { NavigateFunction } from "react-router-dom";
import DeleteIcon from "../../../../../../images/products/trash-blue.svg";
import AddIconBlue from "../../../../../../images/common/add-inside-circle-blue.svg";
import SellerDeletePopup from "../components/DeleteSellerPopup";
import {
  CollapsiblePanel,
  CustomLabel,
  FlexContainer,
  Input,
  IOption,
  ITableHeaders,
  Loader,
  ModalComponent,
  RedButton,
  SapphireBlueButton,
  SingleSelect,
  TableContainer,
  ToastService,
  ToggleSwitch,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import { CountryStore } from "../../../shared/store";
import {
  ApplicationStatus,
  MediaTypes,
  TaxStatus,
} from "../../../shared/enums";
import {
  AuthService,
  MerchantAccountService,
  SellerService,
} from "../../../shared/services";
import {
  DescriptionCell,
  InvoicePopupWrapper,
  VariantWrapper,
} from "../../../shared/styles";
import {
  CountryDropdown,
  CountryPhoneDropdown,
  MediaBoxes,
  MerchantAccountsDropDown,
  TableGrid,
} from "../../../shared/components";

type IProps = {
  navigate: NavigateFunction;
  isEdit?: boolean;
  sellerId?: string;
  onClose: (refresh: boolean, sellerId?: string) => void;
  countryStore: CountryStore;
};

const taxStatusOptions: IOption[] = Object.keys(TaxStatus).map((status) => ({
  label: TaxStatus[status],
  value: status,
}));

const NewSellerPopup: React.FC<IProps> = ({
  onClose,
  isEdit,
  sellerId,
  navigate,
  countryStore,
}) => {
  const [isAPIRequestPending, setAPIRequestPending] = React.useState(false);
  const [data, setData] = useState<CreateSellerModel>(new CreateSellerModel());
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [showMerchantAccountDropdown, setAddMerchantAccount] = useState(false);
  const [merchantAccounts, setMerchantAccounts] = useState<MerchantAccount[]>(
    []
  );
  const [loader, setLoader] = useState(true);

  const countryStates = (data.countryDetails?.states || [])
    .map((state) => {
      return {
        label: state.name,
        value: state.id,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));

  useEffect(() => {
    MerchantAccountService.getMerchantAccounts(
      AuthService.authData.accountId,
      1,
      10000,
      ""
    ).subscribe(
      (response) => {
        if (response.result) {
          const activeMerchantAccounts =
            response.result
              ?.map((m) => MerchantAccount.deserializeAPIModel(m))
              ?.filter(
                (f) => f.applicationStatus === ApplicationStatus.Active
              ) || [];
          setMerchantAccounts(activeMerchantAccounts);
          if (activeMerchantAccounts.length === 1) {
            setAddMerchantAccount(true);
            setData(
              (prevData) =>
                new CreateSellerModel({
                  ...prevData,
                  merchantAccountId:
                    activeMerchantAccounts[0].merchantAccountId,
                  communicationSettings: new SellerCommunicationSettingsModel({
                    ...prevData.communicationSettings,
                    fromEmail: activeMerchantAccounts[0].email,
                  }),
                })
            );
          }
        }
      },
      (error) => {
        console.error(error);
      }
    );
    countryStore.onCountryDataLoaded.subscribe(() => {
      setData(
        (prevData) =>
          new CreateSellerModel({
            ...prevData,
            phoneCountry: countryStore.getUSCountry(),
            countryDetails: countryStore.getUSCountry(),
            taxCountryDetails: countryStore.getUSCountry(),
          })
      );

      loadSellerData();
    });
  }, [isEdit]);

  const loadSellerData = async () => {
    if (isEdit) {
      const response = await SellerService.getSeller(
        AuthService.authData.accountId,
        sellerId || ""
      ).toPromise();
      if (response?.result) {
        const details = CreateSellerModel.deserializeAPIModel(
          response.result,
          countryStore.countriesWithState
        );
        setData(details);
        setLoader(false);
      } else {
        ToastService.showError("Error in getting contact details");
      }
    } else {
      setLoader(false);
    }
    // loadCounties();
  };

  // const loadCounties = () => {
  //     const usCountry = countryStore.getUSCountry();
  //     setData(
  //         (prevData) =>
  //             new CreateSellerModel({
  //                 ...prevData,
  //                 countries: countryStore.countriesWithState,
  //                 phoneCountry: usCountry,
  //                 countryDetails: usCountry,
  //                 taxCountryDetails: usCountry,
  //             }),
  //     );
  // };
  // CountryService.getCountriesWithState().subscribe((response) => {
  //     setData(
  //         (prevData) =>
  //             new CreateSellerModel({
  //                 ...prevData,
  //                 countries: response.result.map((c) => new CountryWithStateModel(c)),
  //                 phoneCountry: response.result.find((c) => c.abbreviationName === 'US'),
  //                 countryDetails: response.result.find((c) => c.abbreviationName === 'US'),
  //                 taxCountryDetails: response.result.find((c) => c.abbreviationName === 'US'),
  //             }),
  //     );
  // });
  // setLoader(false);
  //};

  function row(label: string, rightSide: React.ReactNode, className?: string) {
    {
      return (
        <FlexContainer spacing={40} alignItems="center" className={className}>
          <CustomLabel>{label}</CustomLabel>
          <div>
            <FlexContainer alignItems="center" spacing={20}>
              {rightSide}
            </FlexContainer>
          </div>
        </FlexContainer>
      );
    }
  }

  const formatTaxId = (value: string): string => {
    const sanitizedValue = value.replace(/\D/g, "");
    if (sanitizedValue.length === 0) {
      return "";
    }
    if (sanitizedValue.length <= 1) {
      return sanitizedValue;
    }
    const firstPart = sanitizedValue.slice(0, 2);
    const secondPart = sanitizedValue.slice(2, 9);

    return `${firstPart}-${secondPart}`;
  };

  const handleInputChange = (
    field: keyof CreateSellerModel | keyof SellerCommunicationSettingsModel,
    value:
      | string
      | number
      | boolean
      | Date
      | CountryWithStateModel
      | MediaModel
      | undefined
  ) => {
    if (field === "taxId") {
      const formattedValue = formatTaxId(value as string);
      setData((prevData) => {
        return new CreateSellerModel({ ...prevData, [field]: formattedValue });
      });
    } else {
      switch (field) {
        case "fromEmail":
        case "fromText":
          setData((prevData) => {
            return new CreateSellerModel({
              ...prevData,
              communicationSettings: new SellerCommunicationSettingsModel({
                ...prevData.communicationSettings,
                [field]: value,
              }),
            });
          });
          break;
        default:
          setData((prevData) => {
            return new CreateSellerModel({ ...prevData, [field]: value });
          });
          break;
      }
    }
  };

  const singleSelect = (
    field: keyof CreateSellerModel,
    selectedKey: string,
    className: string,
    options: IOption[],
    error: boolean,
    errorMessage: string
  ): React.ReactNode => {
    return (
      <SingleSelect
        selectedKey={selectedKey}
        hasError={error}
        searchable={field === "state"}
        nameKeys={["label"]}
        valueKeys={["value"]}
        options={[options || []]}
        errorMessage={errorMessage}
        onChange={(option: any) => handleInputChange(field, option.value)}
        className={className}
        searchPlaceholder="Search..."
      />
    );
  };

  const textInput = (
    field: keyof CreateSellerModel | keyof SellerCommunicationSettingsModel,
    type: string,
    value: string | Date,
    className: string,
    error: boolean,
    placeholder: string,
    maxLength?: number
  ) => {
    return (
      <Input
        type={type as "text" | "textarea" | "number" | "date"}
        value={value}
        error={error}
        errorMessage={""}
        maxLength={maxLength}
        className={`styled-input merchant-inputs ${className}`}
        onChange={(e) => handleInputChange(field, e.target.value)}
        placeholder={placeholder}
        onDateChange={(date) => handleInputChange(field, new Date(date))}
      />
    );
  };

  const handleCountryChange = (id: string, field: keyof CreateSellerModel) => {
    if (id) {
      const country = countryStore.countriesWithState.find(
        (country: CountryWithStateModel) => country.id === id
      );
      if (country) {
        handleInputChange(field, country);
      }
    }
  };

  const body = () => {
    return (
      <InvoicePopupWrapper className="new-contact-popup seller-popup">
        {showDeletePopup && (
          <SellerDeletePopup
            sellerData={SellerGridModel.deserializeFromUpsertModel(data)}
            onClose={(refresh: boolean) => {
              refresh && navigate(`/app/payment-settings`);
              setShowDeletePopup(false);
            }}
          />
        )}
        <CollapsiblePanel title="Details">
          <div className="collapsed-invoice-popup">
            {isEdit &&
              row(
                "Status",
                <>
                  <DescriptionCell>
                    <ToggleSwitch
                      on={data.isActive}
                      onChange={(value) => handleInputChange("isActive", value)}
                    />
                  </DescriptionCell>
                </>
              )}
            {row(
              "Name",
              <>
                {textInput(
                  "name",
                  "text",
                  data.name,
                  "",
                  data.hasError("name"),
                  ""
                )}
              </>
            )}
            {row(
              "Country",
              <FlexContainer>
                <CountryDropdown
                  selectedCountry={data.countryDetails}
                  error={data.hasError("countryDetails")}
                  errorMessage={"Please select country"}
                  onCountryChange={(id: string) =>
                    handleCountryChange(id, "countryDetails")
                  }
                  availableCountries={countryStore.sortCountriesByName()}
                />
              </FlexContainer>
            )}
            {row(
              "Address 1",
              <FlexContainer>
                {textInput(
                  "address1",
                  "text",
                  data.address1,
                  "",
                  data.hasError("address1"),
                  ""
                )}
              </FlexContainer>
            )}
            {row(
              "Address 2",
              <FlexContainer>
                {textInput(
                  "address2",
                  "text",
                  data.address2,
                  "",
                  data.hasError("address2"),
                  ""
                )}
              </FlexContainer>
            )}
            {row(
              "City",
              <FlexContainer>
                {textInput(
                  "city",
                  "text",
                  data.city,
                  "",
                  data.hasError("city"),
                  ""
                )}
              </FlexContainer>
            )}
            {row(
              "State",
              <FlexContainer tabIndex={0}>
                {singleSelect(
                  "state",
                  data.state,
                  "default-padding-search-dpdn",
                  countryStates,
                  data.hasError("state"),
                  ""
                )}
              </FlexContainer>
            )}
            {row(
              "Zip",
              <FlexContainer>
                {textInput(
                  "zip",
                  "text",
                  data.zip,
                  "",
                  data.hasError("zip"),
                  ""
                )}
              </FlexContainer>
            )}
            {row(
              "Phone",
              <FlexContainer>
                <CountryPhoneDropdown
                  selectedCountry={data.phoneCountry}
                  error={
                    data.hasError("phoneCountry") ||
                    data.hasError("phoneNumber")
                  }
                  errorMessage={"Please select country and enter phone number"}
                  onCountryChange={(id: string) =>
                    handleCountryChange(id, "phoneCountry")
                  }
                  phoneNumber={data.phoneNumber}
                  onPhoneNumberChange={(number: string) =>
                    handleInputChange("phoneNumber", number)
                  }
                  availableCountries={countryStore.sortCountriesByName()}
                />
              </FlexContainer>
            )}
            {row(
              "Email",
              <FlexContainer>
                {textInput(
                  "email",
                  "text",
                  data.email,
                  "",
                  data.hasError("email"),
                  ""
                )}
              </FlexContainer>
            )}
            {row(
              "Tax Status",
              <FlexContainer>
                {singleSelect(
                  "taxStatus",
                  data.taxStatus,
                  "",
                  taxStatusOptions,
                  data.hasError("taxStatus"),
                  ""
                )}
              </FlexContainer>
            )}
            {row(
              "Tax ID",
              <FlexContainer alignItems="center" spacing={20}>
                <CountryDropdown
                  selectedCountry={data.taxCountryDetails}
                  error={data.hasError("taxCountryDetails")}
                  errorMessage={"Please select country"}
                  onCountryChange={(id: string) =>
                    handleCountryChange(id, "taxCountryDetails")
                  }
                  availableCountries={countryStore.sortCountriesByName()}
                  className="tax-country-dropdown"
                />
                {textInput(
                  "taxId",
                  "text",
                  data.taxId,
                  "",
                  data.hasError("taxId"),
                  ""
                )}
              </FlexContainer>
            )}
            {row(
              "Logo",
              <FlexContainer fxLayout="column">
                <MediaBoxes
                  key={"logo"}
                  hideLabel={true}
                  multiple={false}
                  allowedMediaTypes={[MediaTypes.Image]}
                  medias={data.logo?.fullURL ? [data.logo] : []}
                  onSelectLibraryMedia={(media) =>
                    handleInputChange("logo", media)
                  }
                  onUpload={(medias: MediaModel[]) =>
                    handleInputChange("logo", medias[0])
                  }
                  onDelete={() => handleInputChange("logo", undefined)}
                />
                {data.hasError("logo") && (
                  <p className="error-message">{data.getError("logo")}</p>
                )}
              </FlexContainer>,
              "logo-upload"
            )}
          </div>
        </CollapsiblePanel>
        {renderMerchantAccounts()}
        <CollapsiblePanel title="Communication">
          <div className="collapsed-invoice-popup">
            {row(
              "From Email",
              <FlexContainer>
                {textInput(
                  "fromEmail",
                  "text",
                  data.communicationSettings?.fromEmail,
                  "",
                  data.communicationSettings.hasError("fromEmail"),
                  ""
                )}
              </FlexContainer>
            )}
            {row(
              "From Text",
              <FlexContainer>
                {textInput(
                  "fromText",
                  "text",
                  data.communicationSettings?.fromText,
                  "",
                  data.communicationSettings.hasError("fromText"),
                  "",
                  30
                )}
              </FlexContainer>
            )}
          </div>
        </CollapsiblePanel>
      </InvoicePopupWrapper>
    );
  };

  const renderMerchantAccounts = () => {
    function handleMerchantPrimaryChange(
      merchantAccountId: string,
      isPrimary: boolean
    ) {
      setData((prevData) => {
        const linkedMerchants = prevData.linkedMerchants.map((merchant) => {
          if (merchant.merchantAccountId === merchantAccountId) {
            return new SellerLinkedMerchantAccountModel({
              ...merchant,
              isPrimary: isPrimary,
            });
          } else {
            return new SellerLinkedMerchantAccountModel({
              ...merchant,
              isPrimary: false,
            });
          }
        });
        return new CreateSellerModel({
          ...prevData,
          linkedMerchants: linkedMerchants,
        });
      });
    }
    function onDeleteMerchant(merchantAccountId: string) {
      setData((prevData) => {
        const linkedMerchants = prevData.linkedMerchants.filter(
          (merchant) => merchant.merchantAccountId !== merchantAccountId
        );
        return new CreateSellerModel({
          ...prevData,
          linkedMerchants: linkedMerchants,
        });
      });
    }
    function columns() {
      const columns: ITableHeaders<SellerLinkedMerchantAccountModel>[] = [
        {
          dataKey: "merchantAccountId",
          title: "Name",
          cellColor: "#556bb8",
          cellClassName: "clickable table-col-50",
          className: "table-col-50",
          onCellRender: (item: SellerLinkedMerchantAccountModel) => {
            const merchantAccount = merchantAccounts.find(
              (m) => m.merchantAccountId === item.merchantAccountId
            );
            return (
              <CustomLabel className="standard-lh">
                {merchantAccount?.name}
              </CustomLabel>
            );
          },
        },
        {
          dataKey: "isPrimary",
          title: "Primary",
          cellClassName: "clickable text-center",
          className: "text-center",
          onCellRender: (item: SellerLinkedMerchantAccountModel) => {
            return (
              <Input
                value={item.isPrimary}
                onChange={(e) =>
                  handleMerchantPrimaryChange(
                    item.merchantAccountId,
                    e.target.checked
                  )
                }
                type="radio"
                label={""}
                className={`text-center`}
              />
            );
          },
        },
        {
          dataKey: "merchantAccountId",
          title: "Remove",
          className: "text-center",
          onCellRender: (item: SellerLinkedMerchantAccountModel) => {
            return (
              data.linkedMerchants.length > 1 && (
                <FlexContainer alignItems="center" justifyContent="center">
                  <img
                    src={DeleteIcon}
                    alt="Delete"
                    onClick={() => onDeleteMerchant(item.merchantAccountId)}
                    className="merchant-delete-icon"
                  />
                </FlexContainer>
              )
            );
          },
        },
      ];
      return columns;
    }
    function addMerchantAccount() {
      setData((prevData) => {
        const selectedMerchantAccount = merchantAccounts.find(
          (m) => m.merchantAccountId === prevData.merchantAccountId
        );
        const linkedMerchants = selectedMerchantAccount
          ? [
              ...prevData.linkedMerchants,
              new SellerLinkedMerchantAccountModel({
                merchantAccountId: selectedMerchantAccount.merchantAccountId,
                name: selectedMerchantAccount.name,
                applicationId: selectedMerchantAccount.applicationId,
                gateway: selectedMerchantAccount.gateway,
              }),
            ]
          : prevData.linkedMerchants;
        return new CreateSellerModel({
          ...prevData,
          merchantAccountId: "",
          linkedMerchants: linkedMerchants,
        });
      });
      setAddMerchantAccount(false);
    }
    const fileredMerchantAccounts = merchantAccounts.filter(
      (m) =>
        !data.linkedMerchants.some(
          (lm) => lm.merchantAccountId == m.merchantAccountId
        )
    );

    const selectedMerchantAccount = merchantAccounts.find(
      (m) => m.merchantAccountId === data.merchantAccountId
    );
    return (
      <CollapsiblePanel title="Merchant Accounts">
        <VariantWrapper className="new-seller-popup">
          <div className="collapsed-invoice-popup">
            {data.linkedMerchants?.length > 0 && (
              <TableContainer>
                <TableGrid
                  totalItemCount={0}
                  cellSpacing={20}
                  data={data.linkedMerchants || []}
                  headers={columns()}
                  className="recent-customer-table table item-variant-table variant-table-chckbx"
                />
              </TableContainer>
            )}
            {fileredMerchantAccounts.length > 0 && (
              <FlexContainer alignItems="center">
                <div>
                  {showMerchantAccountDropdown ? (
                    <FlexContainer
                      alignItems="center"
                      spacing={10}
                      onClick={() => setAddMerchantAccount(true)}
                      className="clickable"
                    >
                      <MerchantAccountsDropDown
                        selectedMerchantAccount={selectedMerchantAccount}
                        merchantAccounts={fileredMerchantAccounts}
                        showError={data.hasError("linkedMerchants")}
                        onChange={(option) =>
                          handleInputChange(
                            "merchantAccountId",
                            option.merchantAccountId
                          )
                        }
                      />
                      <CustomLabel
                        color="#556BB8"
                        className="clickable"
                        onClick={() => addMerchantAccount()}
                      >
                        Add
                      </CustomLabel>
                    </FlexContainer>
                  ) : (
                    <FlexContainer
                      alignItems="center"
                      spacing={10}
                      onClick={() => setAddMerchantAccount(true)}
                      className="clickable"
                    >
                      <img src={AddIconBlue} />
                      <CustomLabel color="#556BB8" className="clickable">
                        Add Merchant
                      </CustomLabel>
                    </FlexContainer>
                  )}
                </div>
              </FlexContainer>
            )}
          </div>
        </VariantWrapper>
      </CollapsiblePanel>
    );
  };

  const onClickSave = () => {
    if (isAPIRequestPending) return;

    setAPIRequestPending(true);
    const isValidated = data.validate();

    if (isValidated) {
      const apiModelData = data.createAPIModel();
      if (isEdit) {
        SellerService.updateSeller(apiModelData).subscribe({
          next: (response) => {
            if (response.result) {
              onClose(true, response.result.id);
              ToastService.showSuccess("Seller updated successfully");
            } else {
              ToastService.showError("Error in updating Seller");
            }
            setAPIRequestPending(false);
          },
          error: () => {
            setAPIRequestPending(false);
            ToastService.showError("Error in updating Seller");
          },
        });
        return;
      }
      SellerService.createSeller(apiModelData).subscribe({
        next: (response) => {
          if (response.result?.id) {
            onClose(true, response.result.id);
            ToastService.showSuccess("Seller created successfully");
          } else {
            ToastService.showError("Error in creating Seller");
          }
          setAPIRequestPending(false);
        },
        error: () => {
          setAPIRequestPending(false);
          ToastService.showError("Error in creating Seller");
        },
      });
    } else {
      setAPIRequestPending(false);
      setData((prevData) => new CreateSellerModel({ ...prevData, ...data }));
    }
  };

  return (
    <ModalComponent
      title={isEdit ? "Update Seller" : "New Seller"}
      titleFontSize="22px"
      bodyPadding="40px 90px 40px 60px"
      onClose={() => onClose && onClose(false)}
      showFooter={true}
      wrapperWidth="730px"
      bodyBackgroundColor="#fff"
      footerHeight="auto"
      footerContent={
        <FlexContainer alignItems="center" justifyContent="center" spacing={15}>
          <RedButton
            minWidth="120px"
            height="40px"
            onClick={() => onClose(false)}
          >
            Cancel
          </RedButton>
          <SapphireBlueButton
            primary
            disabled={isAPIRequestPending}
            minWidth="120px"
            height="40px"
            onClick={() => onClickSave()}
          >
            {isEdit ? "Update" : "Save"}
          </SapphireBlueButton>
          {isEdit && (
            <RedButton
              minWidth="120px"
              height="40px"
              onClick={() => setShowDeletePopup(true)}
            >
              Delete
            </RedButton>
          )}
        </FlexContainer>
      }
    >
      {loader ? new Loader(true, "", "380px").loaderComponent : body()}
    </ModalComponent>
  );
};

export default withRouter(observer(NewSellerPopup));
